import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, Loader2, Volume2, Info, Sparkles, X, BookA, ArrowRight, Download, Share2 } from "lucide-react";
import { toPng } from "html-to-image";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Reveal } from "./Reveal";

export const YorubaFinder = () => {
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState("");
  const [featured, setFeatured] = React.useState([]);

  React.useEffect(() => {
    api.get("/yoruba/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const runLookup = async (term) => {
    const q = (term ?? query).trim();
    if (!q) return;
    setQuery(q);
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await api.post("/yoruba/lookup", { query: q });
      setResult(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    runLookup();
  };

  const cardRef = React.useRef(null);
  const [busy, setBusy] = React.useState("");

  const generatePng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#12100e" });
  };

  const downloadCard = async () => {
    setBusy("download");
    try {
      const url = await generatePng();
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${result.term}-meaning.png`;
        a.click();
      }
    } catch (e) {
      /* ignore */
    } finally {
      setBusy("");
    }
  };

  const shareCard = async () => {
    setBusy("share");
    try {
      const url = await generatePng();
      if (!url) return;
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `${result.term}-meaning.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${result.term} — Yorùbá meaning`,
          text: `${result.term}: ${result.meaning} — via ÀsÀNigHT Worldwide`,
        });
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${result.term}-meaning.png`;
        a.click();
      }
    } catch (e) {
      /* ignore (user cancelled share, etc.) */
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="bg-night text-cream grain-overlay" data-testid="yoruba-finder-section">
      <div className="max-w-4xl mx-auto px-6 py-24">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Yorùbá Name & Word Finder</p>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-4">
            What does your name mean?
          </h2>
          <p className="text-cream/70 text-lg leading-relaxed mb-8 max-w-2xl">
            Type any Yorùbá name or word and discover its meaning, literal breakdown,
            pronunciation, and the culture woven into it.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <form onSubmit={onSubmit} className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. Olúwaségun, Adéọlá, or Àṣẹ…"
              maxLength={80}
              data-testid="yoruba-finder-input"
              className="w-full rounded-full bg-night-surface border border-cream/20 pl-6 pr-36 py-5 text-lg text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors"
            />
            <button
              type="submit"
              disabled={loading || !query.trim()}
              data-testid="yoruba-finder-submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-2 bg-gold text-night font-semibold px-6 py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="hidden sm:inline">{loading ? "Searching" : "Find"}</span>
            </button>
          </form>
        </Reveal>

        {/* Featured chips */}
        {featured.length > 0 && !result && !loading && (
          <Reveal delay={0.14}>
            <div className="flex flex-wrap items-center gap-2 mt-6">
              <span className="text-xs text-cream/50 mr-1">Try:</span>
              {featured.map((f) => (
                <button
                  key={f.term}
                  onClick={() => runLookup(f.term)}
                  data-testid={`yoruba-finder-chip-${f.term}`}
                  className="text-sm border border-cream/20 rounded-full px-4 py-1.5 text-cream/80 hover:border-gold hover:text-gold transition-colors"
                >
                  {f.term}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        {/* Browse full dictionary */}
        <Reveal delay={0.18}>
          <Link
            to="/dictionary"
            data-testid="yoruba-finder-browse-link"
            className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-gold hover:text-gold-light transition-colors"
          >
            <BookA className="w-4 h-4" />
            Browse the full A–Z dictionary
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>

        {/* Error */}
        {error && (
          <div
            className="mt-8 flex items-start gap-3 border border-red-400/30 bg-red-500/10 rounded-xl p-5 text-cream/90"
            data-testid="yoruba-finder-error"
          >
            <X className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {/* Result */}
        <AnimatePresence mode="wait">
          {result && (
            <motion.div
              key={result.term}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="mt-10 border border-cream/15 rounded-2xl bg-night-surface overflow-hidden"
              data-testid="yoruba-finder-result"
            >
              <div className="p-8">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="font-serif text-4xl text-gold" data-testid="yoruba-finder-result-term">
                    {result.term}
                  </h3>
                  <span className="text-xs uppercase tracking-widest border border-cream/25 rounded-full px-3 py-1 text-cream/70">
                    {result.type === "name" ? "Name" : "Word"}
                  </span>
                  {result.gender && (
                    <span className="text-xs uppercase tracking-widest border border-cream/25 rounded-full px-3 py-1 text-cream/70">
                      {result.gender}
                    </span>
                  )}
                </div>

                {result.pronunciation && (
                  <p className="flex items-center gap-2 text-cream/60 text-sm mb-6">
                    <Volume2 className="w-4 h-4 text-gold-soft" />
                    <span className="italic">{result.pronunciation}</span>
                  </p>
                )}

                {!result.found ? (
                  <p className="text-cream/80 leading-relaxed">{result.meaning}</p>
                ) : (
                  <div className="space-y-6">
                    <Field label="Meaning" value={result.meaning} />
                    {result.breakdown && <Field label="Literal Breakdown" value={result.breakdown} />}
                    {result.example && <Field label="Example Usage" value={result.example} italic />}
                    {result.cultural_note && (
                      <div className="flex items-start gap-3 border-t border-cream/10 pt-6">
                        <Info className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                        <p className="text-cream/75 leading-relaxed">{result.cultural_note}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {result.found && (
                <div className="px-8 pb-6 flex flex-wrap gap-3" data-testid="yoruba-finder-actions">
                  <button
                    onClick={downloadCard}
                    disabled={busy !== ""}
                    data-testid="yoruba-finder-download"
                    className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
                  >
                    {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download card
                  </button>
                  <button
                    onClick={shareCard}
                    disabled={busy !== ""}
                    data-testid="yoruba-finder-share"
                    className="inline-flex items-center gap-2 border border-cream/25 text-cream font-semibold px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                  >
                    {busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    Share
                  </button>
                </div>
              )}
              {result.found && (
                <p className="text-[11px] text-cream/40 px-8 pb-5">
                  {result.source === "curated"
                    ? "Hand-verified by our editorial team."
                    : "AI-assisted result — Yorùbá is rich in regional and family variation."}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hidden branded card for download/share */}
        {result && result.found && (
          <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
            <div
              ref={cardRef}
              data-testid="yoruba-share-card"
              style={{
                width: 1080,
                height: 1080,
                background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
                color: "#F5EFE6",
                padding: 90,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxSizing: "border-box",
                position: "relative",
              }}
            >
              <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />

              {/* Header */}
              <div style={{ position: "relative" }}>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>
                  ÀsÀNigHT
                </div>
                <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>
                  WORLDWIDE
                </div>
                <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>
                  Yorùbá Name &amp; Word Finder
                </div>
              </div>

              {/* Body */}
              <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
                <div style={{ display: "flex", gap: 14, marginBottom: 18 }}>
                  <span style={{ fontSize: 18, letterSpacing: 3, textTransform: "uppercase", border: "1px solid rgba(245,239,230,0.3)", borderRadius: 999, padding: "6px 18px", color: "rgba(245,239,230,0.75)" }}>
                    {result.type === "name" ? "Name" : "Word"}
                  </span>
                  {result.gender && (
                    <span style={{ fontSize: 18, letterSpacing: 3, textTransform: "uppercase", border: "1px solid rgba(245,239,230,0.3)", borderRadius: 999, padding: "6px 18px", color: "rgba(245,239,230,0.75)" }}>
                      {result.gender}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    color: "#D4A94A",
                    fontSize: result.term.length > 14 ? 76 : result.term.length > 9 ? 104 : 132,
                    lineHeight: 1.02,
                    marginBottom: 16,
                  }}
                >
                  {result.term}
                </div>
                {result.pronunciation && (
                  <div style={{ fontStyle: "italic", fontSize: 32, color: "rgba(245,239,230,0.65)", marginBottom: 28 }}>
                    {result.pronunciation}
                  </div>
                )}
                <div style={{ fontSize: 40, lineHeight: 1.35, color: "#F5EFE6", marginBottom: 20 }}>
                  {result.meaning}
                </div>
                {result.breakdown && (
                  <div style={{ fontSize: 25, lineHeight: 1.4, color: "rgba(245,239,230,0.6)" }}>
                    {result.breakdown}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>
                  Culture is the total way of life…
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, value, italic }) => (
  <div>
    <p className="uppercase tracking-[0.2em] text-[11px] text-gold-soft mb-1.5">{label}</p>
    <p className={`text-cream/85 leading-relaxed text-lg ${italic ? "italic" : ""}`}>{value}</p>
  </div>
);

export default YorubaFinder;
